import React, { FC, useMemo, useState } from 'react';
import { useForm, SubmitHandler, ResolverOptions } from 'react-hook-form';
import { ScheduledEventForm } from './models/ScheduledEventDto';
import {
  ChronoUnit,
  Device,
  Pagination,
  ScheduledEventActivationPayload,
  ScheduledEventDto,
  ScheduledEventMutationPayload,
} from '@thingslog/repositories';
import { devicesQueryClient, scheduledEventsQueryClient } from '../../utils/ReactQueryClients';
import { useToast } from '@thingslog/ui-components';
import { DeviceSelector, ScheduledEventFormUI } from './components';
import { scheduleEventCreateResolver } from './utils';
import { TFunction, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { QueryKeys } from '@thingslog/queries';
import ScheduledTasks from './components/ScheduledTasks';
import { PageHeader, PageHeaderControls, PortalPageShell } from '../../components';
import { useCurrentCompanyId } from '../../hooks';

export const TaskScheduler2: FC<TaskSchedulerProps> = (props: TaskSchedulerProps) => {
  // #region State & Hooks
  const { t } = useTranslation();
  const { scheduledEventId } = useParams();
  const navigate = useNavigate();
  const companyId = useCurrentCompanyId();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [devices, setDevices] = useState<Device[]>([]);
  const [scheduledEvents, setScheduledEvents] = useState<ScheduledEventDto[]>([]);
  const normalizedDevices = useMemo(
    () => devices.map((device: Device) => ({ number: device.number, name: device.name })),
    [devices]
  );
  const headerOptions: PageHeaderControls = {
    showBackButton: true,
    showLanguageButton: true,
    showLogoutButton: true,
    showRedirectThingslogButton: true,
  };

  const form = useForm<ScheduledEventForm>({
    mode: 'all',
    resolver: async (
      values: ScheduledEventForm,
      context: { t: TFunction },
      options: ResolverOptions<ScheduledEventForm>
    ) => scheduleEventCreateResolver(values, { t }, options),
    defaultValues: {
      eventName: '',
      executionHour: 0,
      executionMinute: 0,
      fromDate: new Date(),
      toDate: new Date(),
      repeat: false,
      period: null,
      temporal: ChronoUnit.MINUTES,
      event: {
        '@type': 'SINGLE_RELAY_SWITCH',
        deviceCommand: 'SINGLE_RELAY_SWITCH',
        deviceNumbers: [],
        sensorIndex: 0,
        requiredState: 'OFF',
      },
    },
  });

  const { watch, setValue } = form;

  const selectedRows = watch('event.deviceNumbers');
  // #endregion

  // #region HTTP
  const { useDevicesData } = useMemo(() => devicesQueryClient, []);
  const {
    useAllScheduledEventsInCompany,
    usePatchScheduledEvent,
    useAddScheduledEventsData,
    useDeleteScheduledEvent,
    useGetSpecificScheduledEventById,
    useUpdateScheduledEventData,
  } = useMemo(() => scheduledEventsQueryClient, []);

  const { isLoading: isDevicesLoading } = useDevicesData(companyId ?? 0, 0, 9999, null, null, {
    onSuccess: (data: Pagination<Device>) => {
      setDevices(data.content);
      form.setValue('allDevices', data.content);
    },
    onError: () => {},
    enabled: companyId !== undefined,
    refetchOnWindowFocus: false,
  });

  useGetSpecificScheduledEventById(Number(scheduledEventId), {
    enabled: !!scheduledEventId,
    refetchOnWindowFocus: false,
    onSuccess: (data: any) => {
      const scheduledEvent = data as ScheduledEventDto;
      form.reset({
        eventName: scheduledEvent.eventName,
        fromDate: new Date(scheduledEvent.startDate),
        toDate: new Date(scheduledEvent.endDate),
        repeat: scheduledEvent.repeat,
        period: scheduledEvent.period || null,
        temporal: scheduledEvent.temporal || null,
        executionHour: parseInt(scheduledEvent.executionTime.split(':')[0], 10),
        executionMinute: parseInt(scheduledEvent.executionTime.split(':')[1], 10),
        event: scheduledEvent.scheduleEventDeviceConfig,
      } as ScheduledEventForm);
    },
    onError: (): void => {
      navigate('/taskScheduler');
    },
  });

  const addScheduledEventsData = useAddScheduledEventsData({
    onSuccess: () => {
      refetchAllScheduledEventsInCompany();
      toast({ message: t('task_scheduler_form_create_message'), type: 'success', duration: 3000 });
    },
    onError: () => {
      toast({
        message: t('task_scheduler_form_create_error_message'),
        type: 'error',
        duration: 10000,
      });
    },
  });

  const updateScheduledEventData = useUpdateScheduledEventData({
    onSuccess: () => {
      refetchAllScheduledEventsInCompany();
      toast({ message: t('task_scheduler_form_update_message'), type: 'success', duration: 3000 });
    },
    onError: () => {
      toast({
        message: t('task_scheduler_form_update_error_message'),
        type: 'error',
        duration: 10000,
      });
    },
  });

  const deleteEventMutation = useDeleteScheduledEvent({
    onSuccess: () => {
      refetchAllScheduledEventsInCompany();
      toast({ message: t('task_scheduler_form_delete_message'), type: 'success', duration: 3000 });
    },
  });

  const { refetch: refetchAllScheduledEventsInCompany, isLoading: isScheduledEventsLoading } =
    useAllScheduledEventsInCompany(companyId ?? 0, {
      enabled: companyId !== undefined,
      onSuccess: (data: ScheduledEventDto[]) => {
        setScheduledEvents(data);
      },
      onError: () => {
        toast({
          type: 'error',
          message: t('task_execution_error_loading_message'),
          duration: 10000,
        });
      },
    });

  const activateEventMutation = usePatchScheduledEvent({
    onSuccess() {
      queryClient.invalidateQueries([QueryKeys.GetAllScheduledEventsInCompany]);
    },
    onError(error: AxiosError, variables: { id: number; body: ScheduledEventActivationPayload }) {
      const errorCode: string | undefined = error.response?.data?.errorCode;
      const errorMessage: string | undefined = error.response?.data?.message;
      let toastMessage = t('scheduled_tasks_could_not_update_event_with_id', { id: variables.id });
      if (errorCode === 'INVALID_INPUT') {
        toastMessage = t('scheduled_tasks_could_not_find_event_with_id', { id: variables.id });
      } else if (errorMessage !== undefined) {
        toastMessage = errorMessage;
      }
      toast({
        type: 'error',
        message: toastMessage,
        duration: 10000,
      });
    },
  });

  // #endregion

  // #region Event Handlers
  const onSubmit: SubmitHandler<ScheduledEventForm> = (data: ScheduledEventForm) => {
    if (companyId === undefined) {
      return;
    }

    const scheduledEventFormInstance = new ScheduledEventForm(data);
    const payload: ScheduledEventMutationPayload = scheduledEventFormInstance.toPayload(companyId);
    if (scheduledEventId) {
      updateScheduledEventData.mutate({ id: Number(scheduledEventId), body: payload });
    } else {
      addScheduledEventsData.mutate(payload);
    }
  };

  const handleDevicesSelect = (ids: string[]): void => {
    setValue('event.deviceNumbers', ids, { shouldValidate: true });
  };

  const handleRedirectToEventLogs = (id: number): void => {
    navigate(`/taskScheduler/history/${id}`);
  };

  const handleEditScheduledEvent = (id: number): void => {
    navigate(`/taskScheduler/edit/${id}`);
  };

  const handleChangeEventStatus = (id: number, active: boolean): void => {
    activateEventMutation.mutate({ id, body: { active: active } });
  };

  const handleDeleteScheduledEvent = (id: number): void => {
    deleteEventMutation.mutate(id);
  };

  // #endregion

  // #region Render
  return (
    <>
      <PageHeader title={t('task_scheduler_title')} options={headerOptions} mode="sticky" />
      <PortalPageShell maxWidth="form" className="grid grid-cols-8 gap-3">
        <section className="relative order-2 col-span-8 flex min-h-[16rem] min-w-0 bg-white lg:order-1 lg:col-span-3">
          <div className="absolute inset-0 flex">
            <DeviceSelector
              data={normalizedDevices}
              selectedRows={selectedRows}
              onDevicesSelect={handleDevicesSelect}
              isLoading={isDevicesLoading}
            />
          </div>
        </section>
        <section className="order-1 col-span-8 rounded-md border border-solid border-gray-300 bg-white lg:order-2 lg:col-span-5">
          <ScheduledEventFormUI
            form={form}
            onSubmit={onSubmit}
            mode={scheduledEventId ? 'edit' : 'create'}
          />
        </section>
        <section className="order-3 col-span-8 bg-white">
          <ScheduledTasks
            rows={scheduledEvents}
            isLoading={isScheduledEventsLoading}
            onChangeStatus={handleChangeEventStatus}
            onDelete={handleDeleteScheduledEvent}
            onViewLogs={handleRedirectToEventLogs}
            onEdit={handleEditScheduledEvent}
          />
        </section>
      </PortalPageShell>
    </>
  );
  // #endregion
};

interface TaskSchedulerProps {}
